import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";

import Badge from "../../ui/badge/Badge";
import { useNavigate } from "react-router";
import api from "../../../services/api";
import Pagination from "../../ui/pagination/Pagination";

// Definição da interface do produto
interface Product {
    id: string;
    name: string;
    productId: string;
    imageUrl: string;
    defaultPrice: number;
    type: string;
    status: string;
    pageUrl: string;
}

interface ProductTableProps {
    searchTerm?: string;
    statusFilter?: string;
    type?: "owner" | "coproducer" | "affiliate";
}

export default function ProductTable({
    searchTerm = "",
    statusFilter = "",
    type = "owner"
}: ProductTableProps) {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                setError(null);

                let endpoint = '/api/products';
                const params: Record<string, string> = {};

                // Adicionar filtro de status se fornecido
                if (statusFilter && statusFilter !== "all") {
                    params.status = statusFilter;
                }

                // Definir o endpoint e parâmetros com base no tipo de produto que queremos exibir
                if (type === "owner") {
                    // Buscar produtos onde o usuário é o dono (companyId)
                    // Não precisamos adicionar parâmetros especiais, pois o backend já filtra por companyId
                } else if (type === "coproducer") {
                    // Buscar produtos onde o usuário é co-produtor
                    endpoint = '/api/products/coproductions';
                } else if (type === "affiliate") {
                    // Buscar produtos onde o usuário é afiliado
                    endpoint = '/api/products/affiliates';
                }

                const response = await api.get(endpoint, { params });
                setProducts(response.data.products || []);

            } catch (err: unknown) {
                console.error(`Erro ao buscar produtos (${type}):`, err);
                setError(err instanceof Error ? err.message : "Erro ao carregar produtos");
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [type, statusFilter]); // Adicionar type como dependência para recarregar quando mudar

    // Filtragem com base no termo de busca e ordenação por status
    const filteredProducts = products
        .filter(product => {
            // Filtrar produtos com status "deleted"
            if (product.status === 'deleted') {
                return false;
            }

            return searchTerm === "" ||
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.productId.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => {
            // Definir a prioridade de ordenação: draft > active > inactive > outros
            const statusPriority: Record<string, number> = {
                'draft': 1,
                'active': 2,
                'inactive': 3,
                // Qualquer outro status terá prioridade mais baixa
            };

            // Obter a prioridade de cada produto (ou 999 se não estiver definido)
            const priorityA = statusPriority[a.status] || 999;
            const priorityB = statusPriority[b.status] || 999;

            // Ordenar por prioridade
            return priorityA - priorityB;
        });

    const handleEdit = (productId: string) => {
        navigate(`/products/edit/${productId}`);
    };

    // Função para mudar de páginas
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    // Resetar para a primeira página quando mudar o filtro ou a busca
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, type]);

    if (loading) {
        return <div className="p-4 text-center">Carregando produtos...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    if (filteredProducts.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {searchTerm
                    ? "Nenhum produto encontrado para esta pesquisa."
                    : type === "owner"
                        ? "Sem produtos."
                        : type === "coproducer"
                            ? "Você não possui co-produções."
                            : "Nenhuma afiliação encontrada."}
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    {/* Table Header */}
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 w-24"
                            >
                                Imagem
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Tipo
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>

                        </TableRow>
                    </TableHeader>

                    {/* Table Body */}
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {currentItems.map((product) => (
                            <TableRow
                                key={product.id}
                                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                                onClick={() => type === "owner" ? handleEdit(product.id) : navigate(`/products/view/${product.id}`)}
                            >
                                <TableCell className="px-5 py-4 text-center w-24">
                                    <div className="flex justify-center items-center">
                                        <div className="w-16 h-16 overflow-hidden  rounded-md flex items-center justify-center bg-gray-100">
                                            {product.imageUrl ? (
                                                <img
                                                    width={50}
                                                    height={50}
                                                    src={product.imageUrl}
                                                    alt={`Imagem de ${product.name}`}
                                                    className="object-cover object-center w-16 h-16"
                                                />
                                            ) : (
                                                <div className="text-gray-400 text-xs">Sem imagem</div>
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="px-5 py-4 text-start">
                                    <div>
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                            {product.name}
                                        </span>
                                        <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                            ID: {product.productId}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {product.type === "course" ? "Curso" :
                                        product.type === "subscription" ? "Assinatura" :
                                            product.type === "ebook" ? "E-book" :
                                                product.type === "consultancy" ? "Consultoria" :
                                                    product.type === "mentoring" ? "Mentoria" :
                                                        product.type === "other" ? "Outro" :
                                                            product.type}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    <Badge
                                        size="sm"
                                        color={
                                            product.status === "active"
                                                ? "success"
                                                : product.status === "pending"
                                                    ? "warning"
                                                    : product.status === "draft"
                                                        ? "info"
                                                        : product.status === "inactive"
                                                            ? "error"
                                                            : "error"
                                        }
                                    >
                                        {product.status === "active" ? "Ativo" :
                                            product.status === "pending" ? "Pendente" :
                                                product.status === "inactive" ? "Inativo" :
                                                    product.status === "draft" ? "Rascunho" :
                                                        product.status}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}